import { Component, computed, OnInit, inject } from '@angular/core';
import { DatePickerComponent, DateRange } from '@r1-compass-library/components/date-picker/date-picker.component';
import { ReportChartComponent } from '@r1-compass-library/components/reporting/report-chart/report-chart.component';
import { View } from '@r1-compass-library/models/reporting/chart-data.model';
import { DateTime } from 'luxon';
import { injectParams } from 'ngxtension/inject-params';
import { ReportTableComponent } from '@r1-compass-library/components';
import { PlayReportingService } from './play-reporting.service';
import { PlayStore } from '../../store/play.store';
import { PlayExecutionTableRecord } from '@r1-compass-library/components/reporting/report-table/play-execution-table-record.model';
import { PlaySupportDetailsService } from '@r1-compass-library/services/play-support-details.service';

@Component({
    selector: 'r1-compass-app-play-reporting',
    imports: [ReportChartComponent, DatePickerComponent, ReportTableComponent],
    templateUrl: './play-reporting.component.html',
    styleUrl: './play-reporting.component.scss',
})
export class PlayReportingComponent implements OnInit {
    readonly #playStore = inject(PlayStore);
    readonly #playReportingViewService = inject(PlayReportingService);
    readonly #playSupportDetailsService = inject(PlaySupportDetailsService);

    chartData = this.#playReportingViewService.chartData;
    reportData = this.#playReportingViewService.tableRecords;
    interactionErrorTableData = this.#playReportingViewService.interactionErrorTableData;

    playId = injectParams('id');
    playIdValue = computed(() => Number.parseInt(this.playId()!));
    appSpecificColumns = this.#playReportingViewService.appSpecificColumns;
    appSpecificDetails = this.#playReportingViewService.appSpecificDetails;

    chartView: View = new View(60, 'minutes', 24, 1);
    selectedDate: DateTime = DateTime.now();

    ngOnInit() {
        const playId = this.playId();
        if (playId && playId !== 'new' && this.#playStore.play()?.id !== +playId) {
            this.#playStore.getPlay({ id: +playId });
        }
        this.populateReportingView(this.getCurrentStartTime(), this.getCurrentEndTime());
    }

    private populateReportingView(startRangeIsoString: string, endRangeIsoString: string) {
        this.#playReportingViewService.getReportingData(this.playIdValue(), startRangeIsoString, endRangeIsoString);
    }

    onDateChange(newDateRange: DateRange) {
        this.selectedDate = DateTime.fromISO(newDateRange.start);
        this.populateReportingView(newDateRange.start, newDateRange.end);
    }

    getInteractionErrorTableData(row: PlayExecutionTableRecord) {
        const tableRecords = this.#playReportingViewService.tableRecords();
        if (tableRecords) {
            this.#playReportingViewService.getInteractionErrorTableData(row.playResultId, tableRecords);
        }
    }

    downloadSupportData(row: PlayExecutionTableRecord) {
        const playId = this.playId();
        if (playId) {
            this.#playSupportDetailsService.downloadSupportData(parseInt(playId), row.startDate, row.executionId);
        }
    }

    private getCurrentStartTime(): string {
        const newStartTime = DateTime.now()
            .set({ hour: 0 })
            .set({ minute: 0 })
            .set({ second: 0 })
            .set({ millisecond: 0 })
            .toISO();
        return newStartTime!;
    }

    private getCurrentEndTime(): string {
        const newEndTime = DateTime.now()
            .set({ hour: 23 })
            .set({ minute: 59 })
            .set({ second: 59 })
            .set({ millisecond: 999 })
            .toISO();
        return newEndTime!;
    }
}
